// Copyright (c) Move Closer 2022.

import { AnyObject, Headers, Injectable, IResponse } from '@movecloser/front-core'
import cloneDeep from 'lodash/cloneDeep'
import { DocumentNode } from 'graphql'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'

import { GraphQLMiddleware, IGraphQL, QueryDefinition, QueryDefinitionType, QueryRegistry } from './connector.contracts'
import { IGraphQLDriver, MutationOptions, QueryOptions } from './driver.contracts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
@Injectable()
export class GraphQLConnector implements IGraphQL {
  protected _driver: IGraphQLDriver
  protected _middlewares: GraphQLMiddleware[]
  protected _queries: QueryRegistry

  constructor (queries: QueryRegistry, graphQL: IGraphQLDriver, middlewares: GraphQLMiddleware[] = []) {
    this._driver = graphQL
    this._middlewares = middlewares
    this._queries = queries
  }

  /**
   * @inheritDoc
   */
  public async call (
    queryName: string,
    variables: AnyObject | undefined = undefined,
    headers: Headers = {},
    options: AnyObject = {}
  ): Promise<IResponse> {
    const definition = this.findQueryDefinition(queryName)
    let query = this.buildQuery(definition.type, definition.query, variables, options)

    for (const middleware of this._middlewares) {
      if (typeof middleware.beforeCall !== 'function') {
        continue
      }

      const result = middleware.beforeCall(definition, headers, query)
      const afterBefore = result instanceof Promise ? await result : result

      headers = afterBefore.headers
      query = afterBefore.query
    }

    const response: IResponse = definition.type === 'query'
      ? await this._driver.query(query as QueryOptions, headers)
      : await this._driver.mutate(query as MutationOptions, headers)

    for (const middleware of this._middlewares) {
      if (typeof middleware.afterCall !== 'function') {
        continue
      }

      middleware.afterCall(definition, response)
    }

    return response
  }

  /**
   * Merge given list with existing middlewares.
   */
  public useMiddlewares (list: GraphQLMiddleware | GraphQLMiddleware[]): void {
    this._middlewares = [...this._middlewares, ...(Array.isArray(list) ? list : [list])]
  }

  /**
   * Merge given queries with existing ones.
   */
  public useQueries (queries: QueryRegistry): void {
    this._queries = { ...this._queries, ...queries }
  }

  protected buildQuery (
    type: QueryDefinitionType,
    query: DocumentNode | TypedDocumentNode,
    variables: AnyObject | undefined,
    options: AnyObject
  ): MutationOptions | QueryOptions {
    return {
      [type]: query,
      variables: variables,
      ...options
    } as MutationOptions | QueryOptions
  }

  protected findQueryDefinition (query: string): QueryDefinition {
    const candidate = this._queries[query]
    if (typeof candidate !== 'object' || candidate === null) {
      throw new Error(`[GraphQLConnector]: Cannot find given query [${query}] definition.`)
    }

    return cloneDeep(candidate)
  }
}
